import React, { memo, useCallback, useEffect } from 'react';
import { useKeyPress, useReactFlow, useStore } from 'reactflow';
import useFlowsStore from '../../store/storeFlows';
import { Tooltip } from '@mui/material';

interface EdgeDeleteButtonProps {
  id: string;
  labelX: number;
  labelY: number;
}

function EdgeDeleteButton({ id, labelX, labelY }: EdgeDeleteButtonProps) {
  const onEdgeDelete = useFlowsStore((state) => state.onEdgeDelete);
  const { getEdge, setEdges } = useReactFlow();

  const zoom = useStore((s) => s.transform[2]);
  const isDeletePressed = useKeyPress('Delete');

  const handleDelete = useCallback(
    (event?: React.MouseEvent) => {
      if (event) event.stopPropagation();
      const edge = getEdge(id);
      if (edge) {
        setEdges((edges) => edges.filter((e) => e.id !== id));
        onEdgeDelete([edge], id);
      }
    },
    [id, getEdge, onEdgeDelete, setEdges],
  );

  useEffect(() => {
    if (isDeletePressed) handleDelete();
  }, [isDeletePressed]);

  return (
    <Tooltip title="Delete Edge" placement="top" arrow>
      <button
        type="button"
        onClick={handleDelete}
        style={{
          position: 'absolute',
          transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px) scale(${Math.min(
            1 / zoom,
            3,
          )})`,
          pointerEvents: 'all',
          width: 36,
          height: 36,
          borderRadius: '50%',
          backgroundColor: 'red',
          color: 'white',
          border: '2px solid white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '14px',
          fontWeight: 'bold',
          lineHeight: 1,
          padding: 0,
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          zIndex: 1000,
        }}
      >
        ✕
      </button>
    </Tooltip>
  );
}

export default memo(EdgeDeleteButton);
